'use client';
import React, { useState } from 'react';
import { Filter, X } from 'lucide-react'; // Added X for close button
import AnalysisBlog from '@/components/(blog)/blog/AnalysisBlog';
import Pagination from '@/components/(blog)/blog/Pagination';
import { RelatedPostsCard } from '@/components/(blog)/post/RelatedPostsCard';
import LatestNewsSideBar from '@/components/(postTypes)/news/LatestNewsSideBar';
import { OffsetPagination, PostNode } from '@/types/global';
import { NewsPost } from '@/types/news/types';
import CategorySidebar, { CategoryNode } from '@/components/(blog)/blog/CategorySidebar';
import { PostCardWithExcerpt } from '@/components/(blog)/post/util/PostCardWithExcerpt';

interface LatestBlogProps {
    initialPosts: PostNode[];
    initialPageInfo?: OffsetPagination;
    analyses?: PostNode[];
    pagination?: boolean;
    currentPage: number;
    totalPages: number;
    allNews?: NewsPost[];
    categories?: CategoryNode[];
}

const LatestBlog: React.FC<LatestBlogProps> = ({
    initialPosts,
    analyses,
    allNews,
    categories = [],
    currentPage,
    totalPages,
    pagination,
}) => {
    const [posts] = useState<PostNode[]>(initialPosts);
    // State to handle custom mobile sidebar visibility
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <section className='customContainer flex flex-col gap-10 mt-7 lg:mt-10'>
            {/* --- Custom Mobile Sidebar (CSS Implementation) --- */}
            {/* 1. Backdrop Overlay */}
            <div
                className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
                    isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* 2. Sliding Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-[300px] sm:w-[400px] bg-white dark:bg-gray-900 z-50 shadow-2xl transition-transform duration-300 ease-in-out transform ${
                    isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <div className='p-4 h-full overflow-y-auto'>
                    <div className='flex items-center justify-between mb-6 border-b pb-4 dark:border-white/10'>
                        <h3 className='font-bold text-lg'>دسته‌بندی‌ها</h3>
                        <button
                            onClick={() => setIsMobileMenuOpen(false)}
                            className='p-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors'
                        >
                            <X className='w-5 h-5' />
                        </button>
                    </div>

                    <CategorySidebar initialCategories={categories} mobile={true} />
                </div>
            </div>
            {/* -------------------------------------------------- */}

            <div className='flex gap-4 w-full'>
                {!pagination && (
                    <aside className='hidden xl:block xl:w-3/12'>
                        {/* Desktop Sidebar: Visible only on XL screens */}
                        <CategorySidebar initialCategories={categories} mobile={false} />
                    </aside>
                )}

                <div className={`${pagination ? 'w-full' : 'w-full xl:w-9/12'}`}>
                    {!pagination && (
                        <div className='mb-6 flex items-end justify-between border-b border-black/10 dark:border-white/10'>
                            <h3 className='border-b-2 pb-3 border-progress text-base lg:text-xl'>
                                جدیدترین مقاله‌ها
                            </h3>

                            {/* Mobile Trigger Button */}
                            <div className='xl:hidden pb-2'>
                                <button
                                    onClick={() => setIsMobileMenuOpen(true)}
                                    className='flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md hover:bg-gray-50 dark:hover:bg-white/5 transition-colors border-gray-200 dark:border-white/10'
                                >
                                    <Filter className='w-4 h-4' />
                                    دسته‌بندی‌ها
                                </button>
                            </div>
                        </div>
                    )}

                    <div
                        className={`grid lg:grid-cols-3 gap-2 lg:gap-4 ${pagination ? 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : ''} `}
                    >
                        {posts.map((post, index) => (
                            <div
                                key={index}
                                className='transition-all rounded-xl overflow-hidden border dark:border-white/10 hover:shadow-lg'
                            >
                                {pagination ? (
                                    <RelatedPostsCard
                                        slug={post.uri}
                                        title={post.title}
                                        databaseId={post.databaseId}
                                        featuredImage={post.featuredImage}
                                    />
                                ) : (
                                    <PostCardWithExcerpt
                                        slug={post.uri}
                                        excerpt={post.excerpt}
                                        title={post.title}
                                        databaseId={post.databaseId}
                                        featuredImage={post.featuredImage}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className='mt-5 lg:mt-10'>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            slug={'blog'}
                        />
                    </div>
                </div>
            </div>

            {!pagination && (
                <div className='grid gap-8 pb-10'>
                    {(analyses?.length ?? 0) > 0 && <AnalysisBlog analyses={analyses} />}
                    <LatestNewsSideBar allNews={allNews} />
                </div>
            )}
        </section>
    );
};

export default LatestBlog;
